import type { Phase } from "../../../../../api/spark";
import type { IconName } from "../../../../../design-system/primitives/icons";
import IconButton from "../../../../../design-system/primitives/components/IconButton/IconButton";
import Text from "../../../../../design-system/primitives/components/Text/Text";
import type { NavBag } from "./HighLevelNavigation";
import { StageChip } from "./StageChip";
import styles from "./PhaseGroup.css";

const phaseIcons: Partial<Record<Phase["type"], IconName>> = {
  discover: "route",
  create: "pencil-ruler",
  share: "message-square-share",
};

interface PhaseGroupProps {
  phase: Phase;
  phaseIndex: number;
  nav: NavBag;
}

export const PhaseGroup = ({ phase, phaseIndex, nav }: PhaseGroupProps) => {
  const { currentPhaseIndex, currentStageIndex, disableSome, onStageClick } =
    nav;
  const icon = phaseIcons[phase.type];
  const isCurrentPhase = phaseIndex === currentPhaseIndex;

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        {icon && (
          <IconButton
            icon={icon}
            ariaLabel={phase.name}
            size="large"
            color={isCurrentPhase ? "accent" : "neutral"}
            variant={isCurrentPhase ? "primary" : "secondary"}
            disabled={disableSome && phase.disabled}
            aria-current={isCurrentPhase ? "step" : undefined}
            onClick={() => onStageClick(phaseIndex, 0)}
          />
        )}
        <Text variant="subheader">{phase.name}</Text>
      </div>
      {/* Only the active phase carries the tour anchor. Rendering the id on
          every phase would emit duplicate ids, and the tour resolves
          #chapter-navigation to the first match — always phase 0. */}
      <div
        className={styles.pills}
        id={isCurrentPhase ? "chapter-navigation" : undefined}
      >
        {phase.stages.map((stage, stageIndex) => (
          <StageChip
            key={stage.slug}
            stage={stage}
            isActive={isCurrentPhase && stageIndex === currentStageIndex}
            disabled={disableSome && (phase.disabled || stage.disabled)}
            onClick={() => onStageClick(phaseIndex, stageIndex)}
          />
        ))}
      </div>
    </div>
  );
};
